import { t } from '@lingui/core/macro'
import { IDatasetIntegrationExecution } from '@datasets/dataset-details/datasetHistoryV2/types'
import { formatDate } from '@datasets/dataset-details/datasetHistoryV2/utils'
import { PostStatus, Post } from '@global/types'
import { cn } from '@global/utils/tailwind'
import * as css from './styles'

interface Props {
  post: Post
  assignmentsExecution: Record<number, IDatasetIntegrationExecution[]>
}

enum AlertStatus {
  success = 'success',
  info = 'info',
  warning = 'warning',
  error = 'error',
}

function getAlertStatus(status: PostStatus): AlertStatus {
  if (isPostStatusEqual(status, PostStatus.complete)) return AlertStatus.success
  if (isPostStatusEqual(status, PostStatus.success)) return AlertStatus.success
  if (isPostStatusEqual(status, PostStatus.pending)) return AlertStatus.info
  if (isPostStatusEqual(status, PostStatus.error)) return AlertStatus.error
  return AlertStatus.warning
}

// Check submission status against enum, convert enum to string for evaluation.
function isPostStatusEqual(status: PostStatus, statusType: PostStatus) {
  return status?.toString().toLowerCase() === statusType?.toString()
}

export const Target = ({ post, assignmentsExecution }: Props) => {
  const { target, multi_target, status, error_message, updated_at } = post
  const isStatusSuccess = isPostStatusEqual(status, PostStatus.success)
  const isStatusComplete = isPostStatusEqual(status, PostStatus.complete)
  const isStatusPending = isPostStatusEqual(status, PostStatus.pending)
  const isStatusError = isPostStatusEqual(status, PostStatus.error) || !status

  const alertClasses = cn({
    '-status-success': isStatusComplete,
    '-status-complete': isStatusComplete,
    '-status-pending': isStatusPending,
    '-status-error': isStatusError,
  })

  return (
    <css.Alert severity={getAlertStatus(status)} className={alertClasses} icon={false}>
      {isStatusSuccess ? <css.SuccessAlertTitle>{t`Sync successful`}</css.SuccessAlertTitle> : null}
      {isStatusComplete ? <css.SuccessAlertTitle>{t`Sync complete`}</css.SuccessAlertTitle> : null}
      {isStatusPending ? <css.SuccessAlertTitle>{t`Sync pending`}</css.SuccessAlertTitle> : null}
      {isStatusError ? <css.ErrorAlertTitle>{t`Sync error`}</css.ErrorAlertTitle> : null}
      <css.Divider />
      <css.Content>
        {target ? (
          <>
            <css.TargetName>{target.nickname}</css.TargetName>
            <css.MultiTarget>
              {multi_target?.remote_realm_id ? multi_target.remote_realm_id : target?.realm_name}{' '}
              &gt;&gt;{' '}
              {multi_target?.remote_target_id
                ? multi_target.remote_target_id
                : target?.remote_target_id}
            </css.MultiTarget>
          </>
        ) : (
          <div>
            {t`The target this submission was sent to has been removed from the Claravine platform`}
          </div>
        )}
        <css.LastChecked>
          <strong>{t`Last checked:`}</strong>
          {formatDate(updated_at)}
        </css.LastChecked>
        {post.remote_job_id ? (
          <div>
            <strong>{t`Remote job ID:`}</strong>
            {post.remote_job_id}
          </div>
        ) : null}
        {post.api_response ? (
          <div>
            <strong>{t`API response:`}</strong>
            {post.api_response}
          </div>
        ) : null}
        {error_message ? <div>{error_message}</div> : null}
        {assignmentsExecution &&
          Object.values(assignmentsExecution)
            ?.flat()
            ?.map(exec => (
              <div key={exec.id}>
                <strong>{`${exec['Connector name']}: `}</strong>
                <div>{exec['Created File']}</div>
              </div>
            ))}
      </css.Content>
    </css.Alert>
  )
}
